import React, { useState, useEffect } from 'react';
import { workflowApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowRight, CheckCircle, Gear, TreeStructure } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const WorkflowPage = () => {
  const [pipeline, setPipeline] = useState([]);
  const [config, setConfig] = useState({ stages: [], is_default: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [pipelineRes, configRes] = await Promise.all([workflowApi.getPipeline(), workflowApi.getConfig()]);
      setPipeline(pipelineRes.data);
      setConfig(configRes.data);
    } catch (err) { toast.error('Failed to load workflow data'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await workflowApi.updateConfig({ stages: config.stages });
      toast.success('Workflow configuration saved');
      loadData();
    } catch (err) { toast.error('Failed to save workflow'); }
    finally { setSaving(false); }
  };

  const updateStage = (idx, field, value) => {
    const newStages = [...config.stages];
    newStages[idx] = { ...newStages[idx], [field]: value };
    setConfig(c => ({ ...c, stages: newStages }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;

  return (
    <div className="space-y-6" data-testid="workflow-page">
      <Toaster position="top-right" richColors />
      <div>
        <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="workflow-title">Claim Workflow</h1>
        <p className="text-sm text-slate-500 mt-1">View pipeline status and configure workflow stages</p>
      </div>

      {/* Pipeline View */}
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="workflow-pipeline">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-['Outfit'] text-slate-800 flex items-center gap-2">
            <TreeStructure className="h-5 w-5 text-sky-500" /> Claim Pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex overflow-x-auto gap-2 pb-4">
            {pipeline.map((stage, idx) => (
              <div key={stage.key} className="flex items-center flex-shrink-0">
                <div className="min-w-[140px] rounded-xl p-3 border border-slate-200 bg-white hover:shadow-md transition-all text-center" data-testid={`pipeline-stage-${stage.key}`}>
                  <div className="h-3 w-full rounded-full mb-2 opacity-80" style={{ backgroundColor: stage.color }}></div>
                  <p className="text-xs font-semibold text-slate-700 mb-1">{stage.label}</p>
                  <p className="text-2xl font-bold font-['Outfit']" style={{ color: stage.color }}>{stage.count}</p>
                  {stage.amount > 0 && <p className="text-[10px] text-slate-500 mt-0.5">{formatCurrency(stage.amount)}</p>}
                </div>
                {idx < pipeline.length - 1 && <ArrowRight className="h-4 w-4 text-slate-300 mx-1 flex-shrink-0" />}
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
            <span>Total in pipeline: <strong>{pipeline.reduce((s, p) => s + p.count, 0)}</strong></span>
            <span>&bull;</span>
            <span>Total value: <strong>{formatCurrency(pipeline.reduce((s, p) => s + p.amount, 0))}</strong></span>
          </div>
        </CardContent>
      </Card>

      {/* Workflow Configuration */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="workflow-config">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-['Outfit'] text-slate-800 flex items-center gap-2">
              <Gear className="h-5 w-5 text-violet-500" /> Workflow Stages
            </CardTitle>
            {config.is_default && <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Using Default</Badge>}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {config.stages.map((stage, idx) => (
              <div key={stage.key} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`workflow-stage-${idx}`}>
                <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: stage.color }}>
                  {stage.order}
                </div>
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[10px] text-slate-500">Label</Label>
                    <Input value={stage.label} onChange={e => updateStage(idx, 'label', e.target.value)} className="h-8 text-sm border-slate-200" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Status Key</Label>
                    <Input value={stage.key} disabled className="h-8 text-sm bg-slate-100 border-slate-200 text-slate-500" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Color</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={stage.color} onChange={e => updateStage(idx, 'color', e.target.value)} className="h-8 w-8 rounded cursor-pointer border-0" />
                      <Input value={stage.color} onChange={e => updateStage(idx, 'color', e.target.value)} className="h-8 text-sm font-mono border-slate-200" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={handleSave} disabled={saving} className="mt-4 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white gap-2" data-testid="save-workflow-btn">
            <CheckCircle className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Workflow Configuration'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkflowPage;
